import type {
  BasicDie,
  DerivedStats,
  DieResult,
  EnemyState,
  MasterDie,
  ThrowResult,
} from './types';
import { BALANCE } from '../data/balance';
import { affixValue, affixValueOf, hasAffix, sumAffix } from './affixes';
import { carriedDieSpells, detectSpellsFromDice, grimoireMultiplier, halveSpellBonus } from './combos';
import { backhoeComposition } from './container';
import { corruptedFactor, dieDamageFactor, dieLevelFactor, rollBasicDie, rollMasterDie, type ThrowContext } from './dice';
import { findEffect, isExecutable } from './enemies';
import {
  brokenMultiplier,
  hasMaterialBonus,
  ironBasicMultiplier,
  type ActiveMaterialBonus,
} from './materials';
import type { Rng } from './rng';

/**
 * Tirage **supplémentaire** d'un dé Maître déjà lancé : rebond du *Caoutchouc*
 * (A94-16) ou copie de *42* / *Réplicant* (v0.49.0, A98-2, A98-7). Ses affixes
 * ne comptent qu'une fois, sur le tirage d'origine.
 */
export function isExtraRoll(d: Pick<DieResult, 'bounce' | 'copyOf'>): boolean {
  return d.bounce === true || d.copyOf !== undefined;
}

/**
 * Dés Maîtres d'un lancer, résolus depuis l'inventaire (les inconnus sont
 * ignorés). Un **rebond** (*Caoutchouc*) ou une **copie** (*42*, *Réplicant*)
 * n'est pas un second dé : ses affixes ne comptent qu'une fois (A94-16).
 */
function mastersOf(result: ThrowResult, inventory: readonly MasterDie[]): MasterDie[] {
  const out: MasterDie[] = [];
  for (const d of result.dice) {
    // v0.49.0 : un dé annulé par un boss (`voided`) n'apporte aucun affixe.
    if (!d.isMaster || !d.dieId || isExtraRoll(d) || d.voided) continue;
    const die = inventory.find((x) => x.id === d.dieId);
    if (die) out.push(die);
  }
  return out;
}

/**
 * Construction d'un lancer — **COMMENTS v0.3.0 §1.2** (« Description des
 * dégâts »), reprise verbatim :
 *
 *   `total = D_L × D_C × D_S × M_P × M_T × affixMult`
 *
 *  - `D_L` (§1.2.1) = Σ faces des dés **Maîtres** + Σ faces des dés **Basiques**
 *    + Σ dégâts plats des arbres (*Petit lancer* : +1 par dé Maître équipé et
 *    par niveau). Les faces des dés Basiques comptent **en entier** : le malus
 *    1/5 du Gobelet (spec §11.1.2) n'apparaît plus dans la formule (Q27).
 *  - `D_C` (§1.2.2) = (Σ bonus critiques **additifs**) × (Π bonus critiques
 *    **multiplicatifs**), et **1** si le lancer n'est pas critique. Additifs :
 *    multiplicateur critique de base (1,5) + *Touche critique* + *Dé à lunette*
 *    (chance) + Σ *Incisif* des dés Maîtres du lancer. Multiplicatifs :
 *    *Critique Magique* (`1 + 0,05 × niveau`), premier élément de l'ensemble.
 *  - `D_S` (§1.2.3, réécrit par COMMENTS v0.4.1 §1.4) = Π des multiplicateurs
 *    des sorts déclenchés × *Puissance des sorts*, et **1** sans sort (v0.50.0 :
 *    *Préparation* renforce les sorts des emplacements 1 à 3, A100-2). Calculé par `detectSpellsFromDice` (`core/combos.ts`) :
 *    au plus un sort d'emplacement, plus tous les sorts de dé distincts.
 *  - `M_P` / `M_T` (§1.2.4) = `M_P` vaut 1 (l'arbre de prestige a disparu en
 *    v0.5.0), `M_T` est le produit des multiplicateurs de l'arbre de talents
 *    (*Main lestée* = 1 + 0,05 × niveau).
 *  - `affixMult` : *Exécutant* (1 + Σ X si la cible a plus de 80 % de ses PV,
 *    A122-1 ; « sous 20 % » avant). Hors
 *    formule §1.2, traité comme un multiplicateur d'affixe à part entière —
 *    voir DECISIONS §D.14.
 *  - `minionMult` / `infernoMult` / `primesMult` (v0.7.0) : trois facteurs de
 *    **contexte**, hors formule §1.2 et tracés séparément dans le récapitulatif
 *    — le malus du *Larbin* (×0,75), l'Enfer de *666* (×1,5) et le
 *    multiplicateur en cours de la séquence de *Premiers* (×2, ×3, ×5).
 *  - `electrifyMult` / `archidicerMult` (v0.57.0, A122-6, A122-7) :
 *    *Électrification* de *Silicium 5* (+1 % par cumul, 50 au plus) et
 *    *ArchiDicer* (+33 % par sort du lancer, +100 % au plus), dans le même
 *    produit que les autres facteurs.
 *  - `croupierMult` (v0.58.0, A123-3) : *Croupier* équipé, ×0,01 ou ×4 à une
 *    chance sur deux, tiré à chaque lancer (Larbin compris), même produit.
 *  - `affixFactor` (v0.58.0, A123-4) : *Rat-Ailé* actif, ×1,2 sur les affixes
 *    à plage des dés (Lesté, Incisif, Précis, Exécutant ici ; Empoisonné et
 *    son plafond, Mutilant, Temporel, Doré à l'application, lus sur le lancer).
 *
 * Fonction pure : rien n'est modifié en dehors du `Rng`.
 *
 * `container` : dés Basiques du Gobelet, lancés **après** les dés Maîtres (leur
 * ordre importe : le bouclier absorbe toujours le dé d'index 0, donc un dé
 * Maître). Ils ne comptent ni pour les combinaisons ni pour le critique.
 */
export interface ThrowOptions {
  /** Le lancer est-il celui du *Larbin* ? (dégâts ×0,75 — A18) */
  byMinion?: boolean;
  /** L'Enfer de *666* brûle-t-il pendant ce lancer ? (dégâts ×1,5) */
  inferno?: boolean;
  /** Multiplicateur en cours de la séquence de *Premiers* (1 = aucun). */
  primesMult?: number;
  /**
   * Orc Mage (§F) : nombre d'emplacements **retirés** du
   * lancer par l'ennemi. Les dés correspondants ne sont pas dans `masters` —
   * l'appelant (`Game`) les a déjà écartés ; l'information n'est ici que pour
   * le récapitulatif (`breakdown.blockedSlots`).
   */
  blockedSlots?: number;
  /**
   * *Dice Master* (A72-5) : multiplicateur du lancer, moyenne des facteurs de
   * rareté des Maîtres **équipés** (`diceMasterMultiplier`) ; 1 par défaut.
   */
  diceMasterMult?: number;
  /** *Criticité* : critiques consécutifs **avant** ce lancer (0 par défaut). */
  critStreak?: number;
  /** *Flux arcanique* : lancers consécutifs à sort **avant** ce lancer. */
  spellStreak?: number;
  /**
   * *ArchiDicer* équipé : v0.57.0 (A122-7), +33 % de dégâts par sort du
   * lancer, +100 % au plus (`breakdown.archidicerMult`). La garantie d'un sort
   * de dé (A72-16) a disparu.
   */
  archidicer?: boolean;
  /**
   * *Tractopelle* (A72-17) : dés Basiques **supplémentaires** du lancer, de
   * même composition que `container` (0 par défaut).
   */
  backhoe?: number;
  /** *Guinsoo* (A72-15) : coups du lancer (2 = dégâts appliqués deux fois). */
  hits?: number;
  /**
   * *Arrière-garde* (A89-6) : identifiants des dés des **deux derniers
   * emplacements équipés** (un seul s'il n'y a qu'un dé). Leurs sorts de dé de
   * dégâts sont renforcés si `stats.rearguardBoost > 0`.
   */
  rearguardDieIds?: readonly string[];
  /**
   * *Préparation* (v0.50.0, A100-2) : identifiants des dés Maîtres **équipés
   * dans les emplacements 1 à 3** (`Game.prepSlots()`). Leurs sorts de dé sont
   * renforcés si `stats.prepBoost > 0`. Remplace l'ancien compteur `prep`.
   */
  prepDieIds?: readonly string[];
  /**
   * Matériaux (v0.47.0, A94-16, A95) : bonus actifs et mémoire du combat,
   * construits par `Game.rollThrow`. Absent : aucun bonus.
   */
  materials?: MaterialContext;
  /**
   * *42* (v0.49.0, A98-2) : identifiants des dés à lancer **une fois de plus**
   * (`state.combat.fortyTwoCopies`) ; un dé absent du lancer n'est pas copié.
   */
  fortyTwoCopies?: readonly string[];
  /** *Rouleau Compresseur* (A98-4) : plus haute face Maître du lancer précédent appliqué. */
  lastMaxFace?: number;
  /**
   * *Croupier* équipé (v0.58.0, A123-3) : le lancer tire ×0,01 ou ×4 (une
   * chance sur deux) — le `Rng` n'est consommé que dans ce cas.
   */
  croupier?: boolean;
  /**
   * Facteur d'affixes du lancer (v0.58.0, A123-4) : ×1,2 du *Rat-Ailé* actif
   * (dé équipé, aucun bonus de Matériau), 1 par défaut. Publié dans
   * `breakdown.affixFactor` pour `poisonOf`, `mutilationRoll`, `killBonuses`.
   */
  affixFactor?: number;
}

/**
 * Contexte des **Matériaux** d'un lancer (v0.47.0) — tout ce qui est
 * « précédent » ou « reporté » vient de `state.combat`, jamais sauvegardé.
 */
export interface MaterialContext {
  /** Bonus actifs (`core/materials.ts : activeMaterialBonuses`). */
  bonuses: readonly ActiveMaterialBonus[];
  /** *Boisé 5* : somme propre des faces Basiques du lancer précédent appliqué. */
  lastBasicSum: number;
  /**
   * *Silicium 5* — cumuls d'*Électrification* (A122-6), celui de ce lancer
   * compris (`Game.rollThrow` l'ajoute avant le calcul). Ignoré sans le bonus.
   */
  electrifyStacks?: number;
  /** *Du Vide 5* : total propre du lancer précédent appliqué. */
  lastTotal: number;
  /** *Silicium 3* : excédent du dernier coup fatal, à ajouter aux dégâts finaux. */
  overkillCarry: number;
  /** *Osseux* : niveau de bris de l'ennemi visé (0 = intact). */
  broken: number;
  /**
   * *Caoutchouc* : identifiants des dés qui **rebondissent** (emplacement 1,
   * puis 1 à 3), dans l'ordre des emplacements ; seuls les dés présents dans
   * le lancer rebondissent. Calculé par `Game` (qui connaît les emplacements).
   */
  bounceDieIds?: readonly string[];
}

/**
 * *Dice Master* (A72-5) : moyenne des facteurs de rareté
 * (`BALANCE.diceMasterFactors`) des dés Maîtres **équipés** — 1 sans dé.
 */
export function diceMasterMultiplier(equipped: readonly MasterDie[]): number {
  if (equipped.length === 0) return 1;
  let sum = 0;
  for (const die of equipped) sum += BALANCE.diceMasterFactors[die.rarity] ?? 1;
  return sum / equipped.length;
}

/**
 * *ArchiDicer* (v0.57.0, A122-7) : `1 + min(archidicerCap, archidicerPerSpell
 * × sorts)` — ×1 sans sort, ×1,33 pour un, ×1,99 pour trois, ×2 au-delà.
 */
export function archidicerMultiplier(spells: number): number {
  const n = Math.max(0, Math.floor(spells));
  return 1 + Math.min(BALANCE.archidicerCap, BALANCE.archidicerPerSpell * n);
}

/**
 * *Croupier* (v0.58.0, A123-3) : un tirage du `Rng` — `croupierHigh` (×4) à
 * `croupierChance` (0,5), `croupierLow` (×0,01) sinon.
 */
export function croupierRoll(rng: Rng): number {
  return rng.chance(BALANCE.croupierChance) ? BALANCE.croupierHigh : BALANCE.croupierLow;
}

/** Facteur d'affixes publié par un lancer (`breakdown.affixFactor`, A123-4) : 1 s'il manque. */
export function affixFactorOf(result: Pick<ThrowResult, 'breakdown'>): number {
  const f = result.breakdown?.affixFactor;
  return typeof f === 'number' && f > 0 ? f : 1;
}

/**
 * Multiplicateur d'une **série** (*Criticité*, *Flux arcanique* — A72-3, 4) :
 * `facteur^(série − 1)` dès le 2e coup consécutif, 1 avant. v0.49.0
 * (COMMENTS v0.48.1 §1.2, A98-1) : **plafonné à 10 cumuls**,
 * `facteur^min(série − 1, BALANCE.streakMaxStacks)` — la série 11 vaut la 10e
 * cumulée, la 12 aussi.
 */
export function streakMultiplier(factor: number, streak: number): number {
  if (!(factor > 1) || streak < 2) return 1;
  return Math.pow(factor, Math.min(streak - 1, BALANCE.streakMaxStacks));
}

/**
 * Somme des faces de `count` dés à `sides` faces. Tirage **exact** dé par dé
 * jusqu'à `BALANCE.backhoeExactLimit` ; au-delà, approximation normale
 * (moyenne `n(s+1)/2`, variance `n(s²−1)/12`), arrondie et bornée à
 * `[n, n × s]` — le tractopelle n'a pas de plafond (A72-17).
 */
export function rollDiceSum(sides: number, count: number, rng: Rng): number {
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return 0;
  if (n <= BALANCE.backhoeExactLimit) {
    let sum = 0;
    for (let i = 0; i < n; i++) sum += rng.die(sides);
    return sum;
  }
  const mean = (n * (sides + 1)) / 2;
  const sd = Math.sqrt((n * (sides * sides - 1)) / 12);
  // Box-Muller : deux tirages uniformes du `Rng` (déterministe).
  const u1 = Math.max(Number.EPSILON, rng.next());
  const u2 = rng.next();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.min(n * sides, Math.max(n, Math.round(mean + sd * z)));
}

export function buildThrow(
  id: number,
  masters: readonly MasterDie[],
  stats: DerivedStats,
  enemy: EnemyState | null,
  rng: Rng,
  container: readonly BasicDie[] = [],
  opts: ThrowOptions = {},
): ThrowResult {
  const executing = isExecutable(enemy);
  const affixFactor = opts.affixFactor !== undefined && opts.affixFactor > 0 ? opts.affixFactor : 1;
  const ctx: ThrowContext = {
    stats,
    executing,
    executionerHpAbove: BALANCE.executionerHpAbove,
    affixFactor,
  };

  const mat = opts.materials ?? null;
  const bonuses = mat?.bonuses ?? [];

  const dice: DieResult[] = [];
  for (const die of masters) dice.push(rollMasterDie(die, ctx, rng));
  /**
   * *Caoutchouc* (A94-16) : chaque dé qui rebondit est lancé une **seconde**
   * fois (Pipé, Lesté, Avantageux compris) ; la face rejoint `dice` à la suite
   * des Maîtres, marquée `bounce`. Elle compte dans `D_L` (niveau compris, sans
   * dégât plat : ce n'est pas un dé équipé de plus) et dans les sorts ; les
   * affixes du dé ne comptent qu'une fois.
   */
  for (const id of mat?.bounceDieIds ?? []) {
    const die = masters.find((m) => m.id === id);
    if (!die) continue;
    const again = rollMasterDie(die, ctx, rng);
    again.bounce = true;
    again.damage = again.face * dieDamageFactor(die, ctx.stats);
    dice.push(again);
  }
  /**
   * **Copies** (v0.49.0) : un dé copié est lancé une fois de plus, exactement
   * comme un rebond — face dans `D_L` (niveau et ×1000 Corrupted compris, sans
   * dégât plat) et dans les sorts, affixes une fois. *42* (A98-2) : les dés
   * qui ont fait le 4 et le 2 au lancer précédent ; *Réplicant* (A98-7) : chaque
   * dé qui porte l'affixe. Une copie par source, jamais de copie de copie.
   */
  const rollCopy = (die: MasterDie, source: '42' | 'replicant'): void => {
    const again = rollMasterDie(die, ctx, rng);
    again.copyOf = source;
    again.damage = again.face * dieDamageFactor(die, ctx.stats);
    dice.push(again);
  };
  for (const id of opts.fortyTwoCopies ?? []) {
    const die = masters.find((m) => m.id === id);
    if (die) rollCopy(die, '42');
  }
  // *Réplicant* (A98-7) : en plus d'un rebond ou d'une copie de *42*, jamais récursif.
  for (const die of masters) if (hasAffix(die, 'replicant')) rollCopy(die, 'replicant');
  // *Silicium 5* : le dé fantôme (A94-16) a disparu en v0.57.0 (A122-6),
  // remplacé par l'*Électrification* (`electrifyMult`, plus bas).
  for (const die of container) dice.push(rollBasicDie(die, rng));

  /**
   * Reine des Sirènes, Ancien Roi Maudit, Tricorne (v0.49.0, COMMENTS v0.48.1
   * §1.8) : `count` dés Maîtres **lancés** tirés au hasard (au `Rng`, sans
   * remise) sont **annulés** — toutes leurs faces (rebond et copies comprises)
   * sont absorbées et marquées `voided` : ni dégâts, ni sorts, ni affixes.
   * Distinct du bouclier, qui absorbe toujours le premier dé.
   */
  const voidEffect = findEffect(enemy, 'voidDice');
  const voidedIds = new Set<string>();
  if (voidEffect && voidEffect.count > 0) {
    const pool = masters.map((m) => m.id);
    for (let i = 0; i < voidEffect.count && pool.length > 0; i++) {
      voidedIds.add(pool.splice(rng.int(0, pool.length - 1), 1)[0]);
    }
    for (const d of dice) {
      if (d.isMaster && d.dieId && voidedIds.has(d.dieId)) {
        d.absorbed = true;
        d.voided = true;
        d.damage = 0;
      }
    }
  }
  /** Dés Maîtres qui jouent vraiment : sorts portés et affixes (dés annulés exclus). */
  const playing = voidedIds.size > 0 ? masters.filter((m) => !voidedIds.has(m.id)) : masters;

  // Squelette à bouclier — **A26** : le premier dé **Maître** du lancer est
  // annulé (0 dégât, pas de dégât plat) **tant que l'ennemi vit**, le bouclier
  // ne se brise plus. Sa face compte quand même pour les combinaisons (§D.8).
  // Les dés Basiques sont lancés après les Maîtres et passent toujours ; si les
  // emplacements bloqués (Orc Mage) ne laissent aucun dé Maître, rien n'est
  // absorbé. v0.49.0 : le Roi squelette porte aussi le bouclier ; un dé déjà
  // annulé (`voided`) est sauté.
  if (enemy?.shield) {
    const first = dice.findIndex((d) => d.isMaster && !d.voided);
    if (first >= 0) {
      dice[first].absorbed = true;
      dice[first].damage = 0;
    }
  }

  // ── D_S : les sorts sont évalués sur les faces des dés Maîtres.
  /**
   * *Arrière-garde* (A89-6) : sorts de dé portés par les dés des deux derniers
   * emplacements équipés **présents dans le lancer** (un dé bloqué ou annulé ne
   * porte rien). *Préparation* (v0.50.0, A100-2) : de même pour les dés des
   * emplacements 1 à 3. Mage Maudit (A100-1) : la part de bonus de chaque sort
   * est divisée **en dernier** (`halveSpells`), après les deux renforts et les
   * Grimoires.
   */
  const rearguardBoost = stats.rearguardBoost ?? 0;
  const rearguardIds = opts.rearguardDieIds ?? [];
  const rearguard =
    rearguardBoost > 0 && rearguardIds.length > 0
      ? {
          spells: carriedDieSpells(playing.filter((m) => rearguardIds.includes(m.id))),
          boost: rearguardBoost,
        }
      : null;
  const prepBoost = stats.prepBoost ?? 0;
  const prepIds = opts.prepDieIds ?? [];
  const prep =
    prepBoost > 0 && prepIds.length > 0
      ? { spells: carriedDieSpells(playing.filter((m) => prepIds.includes(m.id))), boost: prepBoost }
      : null;
  const halveEffect = findEffect(enemy, 'halveSpells');
  const halveSpells = halveEffect ? Math.max(1, halveEffect.divisor) : 1;
  const spellsHalved = halveSpells > 1;
  const lastMaxFace = Math.max(0, opts.lastMaxFace ?? 0);
  const spellOpts = { ...opts, rearguard, prep, halveSpells, lastMaxFace };
  // v0.57.0 (A122-7) : l'*ArchiDicer* ne force plus de sort (`archidicerMult`).
  const outcome = detectSpellsFromDice(dice, playing, stats, spellOpts);
  /**
   * `D_S` : les renforts (*Arrière-garde*, *Préparation*, Grimoires) et la
   * division du Mage Maudit (v0.50.0, A100-1 — il annulait tout depuis A31)
   * sont déjà dans les multiplicateurs des sorts.
   */
  const DS = outcome.multiplier;
  const spellEffectMultipliers = outcome.effectMultipliers;

  /**
   * *Vingt-dicte* (v0.49.0, A98-4) : ce lancer lance **le double** de dés
   * Basiques — un second contenant, tiré après les sorts (le `Rng` n'est
   * consommé de plus que si le sort sort), et le Tractopelle doublé. v0.50.0
   * (A100-1) : le Mage Maudit n'y touche plus.
   */
  const basicDoubled =
    outcome.spells.some((sp) => sp.id === 'twentyDict') && container.length + (opts.backhoe ?? 0) > 0;
  if (basicDoubled) for (const die of container) dice.push(rollBasicDie(die, rng));

  // ── D_L : faces des Maîtres + faces des Basiques + dégâts plats.
  // v0.46.0 (A94-5) : la face d'un Maître à niveau pèse `face × 1,5^niveau` ;
  // `baseMaster` garde les faces brutes, le surplus va dans `dieLevelBonus`.
  // v0.47.0 : le niveau se lit par `dieId` (un rebond garde celui de son dé) ;
  // `ironMaxFaces` compte les faces Maîtres maximales des dés réellement
  // lancés (rebonds compris).
  const levelOf = new Map(masters.map((m) => [m.id, dieLevelFactor(m, stats)]));
  /**
   * v0.49.0 (A98-6) : dé **Corrupted**, face × 1,5^niveau × 1000 — le ×1000
   * est tracé à part (`corruptedBonus`), copies et rebonds compris.
   */
  const corruptOf = new Map(masters.map((m) => [m.id, corruptedFactor(m, stats)]));
  let baseMaster = 0;
  let dieLevelBonus = 0;
  let corruptedBonus = 0;
  let rawBasic = 0;
  let flat = 0;
  let ironMaxFaces = 0;
  for (const d of dice) {
    if (d.absorbed) continue;
    if (d.isMaster) {
      baseMaster += d.face;
      const factor = d.dieId ? (levelOf.get(d.dieId) ?? 1) : 1;
      if (factor !== 1) dieLevelBonus += d.face * (factor - 1);
      const corrupt = d.dieId ? (corruptOf.get(d.dieId) ?? 1) : 1;
      if (corrupt !== 1) corruptedBonus += d.face * factor * (corrupt - 1);
      // *Petit lancer* : +1 dégât de base **par dé Maître équipé** et par niveau
      // (ni le rebond, ni une copie ne sont des dés équipés de plus).
      if (!isExtraRoll(d)) flat += stats.flatDamagePerDie;
      if (d.face >= d.sides) ironMaxFaces += 1;
    } else {
      rawBasic += d.damage;
    }
  }
  // Tortue Épineuse Gigantesque (**A31**) : la somme des faces des dés
  // **Basiques** est divisée par 2 dans `D_L`. `baseBasic` porte la valeur
  // effective, `basicDivisor` garde la trace de la division.
  // *Tractopelle* (A72-17) : dés Basiques supplémentaires de même composition
  // que le contenant. Ils ne sont pas listés dans `dice` (le compteur n'a pas
  // de plafond) : seule la somme de leurs faces entre dans `D_L`.
  const backhoe = Math.max(0, Math.floor(opts.backhoe ?? 0)) * (basicDoubled ? 2 : 1);
  let backhoeBasic = 0;
  if (backhoe > 0) {
    for (const part of backhoeComposition(container, backhoe)) {
      backhoeBasic += rollDiceSum(part.sides, part.count, rng);
    }
    rawBasic += backhoeBasic;
  }
  const halve = findEffect(enemy, 'halveBasic');
  let basicDivisor = halve ? Math.max(1, halve.divisor) : 1;
  /**
   * Saigneur Vampire, Tricorne (v0.49.0) : `chance`, tirée à chaque lancer
   * (le `Rng` n'est consommé que contre eux), de diviser aussi la somme des
   * faces Basiques par `divisor`.
   */
  const halveChance = findEffect(enemy, 'halveBasicChance');
  if (halveChance && rng.chance(halveChance.chance)) basicDivisor *= Math.max(1, halveChance.divisor);
  const ownBasic = rawBasic / basicDivisor;
  /**
   * *Boisé 5* : la somme des faces Basiques devient `max(actuelle,
   * précédente)` — « précédente » étant la somme **propre** du lancer
   * précédent appliqué du combat ; le report est tracé dans `woodBasicCarry`.
   * *Ferreux 3 / 5* : puis × 1,25 et × 2^k (`ironBasicMult`).
   */
  const woodBasicCarry = hasMaterialBonus(bonuses, 'wood', 5)
    ? Math.max(0, (mat?.lastBasicSum ?? 0) - ownBasic)
    : 0;
  const ironBasicMult = ironBasicMultiplier(bonuses, ironMaxFaces);
  const baseBasic = (ownBasic + woodBasicCarry) * ironBasicMult;
  const DL = baseMaster + dieLevelBonus + corruptedBonus + baseBasic + flat;

  // ── D_C : un seul tirage de critique pour tout le lancer.
  // Les affixes *Précis* / *Incisif* de tous les dés Maîtres du lancer
  // s'additionnent (y compris ceux du dé absorbé : il a bien été lancé — D.8).
  const critAdditive: number[] = [stats.critMult];
  for (const die of playing) {
    const incisive = affixValue(die, 'incisive', affixFactor);
    if (incisive > 0) critAdditive.push(incisive);
  }
  /**
   * Ensemble **multiplicatif** du critique (§1.2.2) : *Critique Magique*
   * (`ars_magiccrit`) en est le premier élément — COMMENTS v0.6.0 §1.1. Le
   * facteur neutre (1) n'est pas listé, pour ne pas polluer le récapitulatif.
   */
  const critMultipliers: number[] = [];
  if (stats.critMultFactor > 1) critMultipliers.push(stats.critMultFactor);
  const critChance = stats.critChance + sumAffix(playing, 'precise', affixFactor);
  // *Avantage critique* (A72-7) : deux tirages, critique si l'un réussit (le
  // second n'est tiré que si le premier a échoué).
  const crit = rng.chance(critChance) || (stats.critAdvantage === true && rng.chance(critChance));
  const critSum = critAdditive.reduce((a, b) => a + b, 0);
  const critProduct = critMultipliers.reduce((a, b) => a * b, 1);
  /**
   * Mygale ÷2, Gobelin Arbalétrier ÷3, Vautour ÷4 — **A27** : c'est le
   * *multiplicateur* critique qui est divisé, avec un **plancher à ×1** : un
   * critique n'est jamais pire qu'un lancer normal.
   */
  const critDivide = findEffect(enemy, 'critDivide');
  const critDivisor = critDivide ? Math.max(1, critDivide.divisor) : 1;
  const DC = Math.max(1, (crit ? critSum * critProduct : 1) / critDivisor);

  // ── M_P, M_T et le multiplicateur d'affixe du lancer.
  const MP = 1;
  const MT = stats.damageMult;
  const affixMult = executing ? 1 + sumAffix(playing, 'executioner', affixFactor) : 1;

  // ── Facteurs de contexte (v0.7.0) : Larbin, Enfer, Premiers.
  // L'Enfer et *Premiers* sont des multiplicateurs de **sorts** : le Mage
  // Maudit divise aussi leur part de bonus, au moment où ils s'appliquent
  // (v0.50.0, A100-1 : ×1,5 → ×1,25 ; ×2, ×3, ×5 → ×1,5, ×2, ×3).
  // *Larbin* : ×0,75, relevé par *Esclavagisme* (A72-12).
  const minionMult = opts.byMinion ? (stats.minionDamageFactor ?? BALANCE.minionDamageFactor) : 1;
  // *Grimoire des nombres* (A94-11) : l'Enfer et le multiplicateur de
  // *Premiers* sont renforcés au moment où ils s'appliquent.
  const numbers = stats.grimoireNumbers ?? 0;
  const infernoBase = opts.inferno ? BALANCE.spellInfernoMultiplier : 1;
  const primesBase = opts.primesMult && opts.primesMult > 0 ? opts.primesMult : 1;
  const infernoGrimoire = grimoireMultiplier(infernoBase, numbers);
  const primesGrimoire = grimoireMultiplier(primesBase, numbers);
  // Grimoire d'abord, Mage Maudit ensuite (même ordre que les sorts du lancer).
  const infernoMult = halveSpellBonus(infernoGrimoire, halveSpells);
  const primesMult = halveSpellBonus(primesGrimoire, halveSpells);
  // Facteurs du Grimoire seul (avant la division du Mage Maudit).
  const grimoireNumbersMult =
    outcome.grimoireNumbersMult * (infernoGrimoire / infernoBase) * (primesGrimoire / primesBase);
  const grimoireFacesMult = outcome.grimoireFacesMult;

  // ── v0.26.0 (A72) : Dice Master, Criticité, Flux arcanique.
  const diceMasterMult =
    opts.diceMasterMult && opts.diceMasterMult > 0 ? opts.diceMasterMult : 1;
  const critStreak = crit ? Math.max(0, opts.critStreak ?? 0) + 1 : 0;
  const spellStreak = outcome.spells.length > 0 ? Math.max(0, opts.spellStreak ?? 0) + 1 : 0;
  const critStreakMult = streakMultiplier(stats.critStreakFactor ?? 1, critStreak);
  const spellStreakMult = streakMultiplier(stats.spellStreakFactor ?? 1, spellStreak);

  // *Osseux* (A94-14) : contre un ennemi brisé, après les multiplicateurs habituels.
  const brokenMult = brokenMultiplier(mat?.broken ?? 0);
  // *Silicium 5* — *Électrification* (A122-6) : +1 % par cumul, 50 au plus.
  const electrifyStacks = hasMaterialBonus(bonuses, 'silicon', 5)
    ? Math.min(BALANCE.siliconElectrifyMax, Math.max(0, Math.floor(mat?.electrifyStacks ?? 0)))
    : 0;
  const electrifyMult = 1 + BALANCE.siliconElectrifyStep * electrifyStacks;
  /**
   * *ArchiDicer* (A122-7) : `1 + min(cap, 0,33 × n)`, `n` = tous les sorts du
   * lancer (emplacement et dé, ceux des copies de *42*, *Réplicant* et des
   * rebonds compris). Un affixe : le Mage Maudit ne le divise pas.
   */
  const archidicerMult = opts.archidicer ? archidicerMultiplier(outcome.spells.length) : 1;
  /**
   * *Croupier* (v0.58.0, A123-3) : ×4 à `croupierChance` (une chance sur
   * deux), ×0,01 sinon — tiré à chaque lancer, Larbin compris ; le `Rng` n'est
   * consommé que si le dé est équipé. Un affixe : le Mage Maudit ne le divise pas.
   */
  const croupierMult = opts.croupier ? croupierRoll(rng) : 1;

  const own =
    DL *
    DC *
    DS *
    MP *
    MT *
    affixMult *
    minionMult *
    infernoMult *
    primesMult *
    diceMasterMult *
    critStreakMult *
    spellStreakMult *
    brokenMult *
    electrifyMult *
    archidicerMult *
    croupierMult;
  /**
   * Marque de *Null* (v0.49.0, A98-3) : le lancer qui déclenche Null contre un
   * monstre **déjà marqué** libère les dégâts cumulés — ajoutés au total, ×
   * *Osseux* ; *Du Vide 5*, *Silicium 3* et l'armure s'appliquent ensuite au
   * total libéré. La pose de la marque et le cumul (v0.50.0 : les lancers
   * marqués frappent **et** s'accumulent) se font à l'application
   * (`Game.applyThrow`).
   */
  const mark = enemy?.nullMark ?? null;
  const releases = mark !== null && outcome.spells.some((sp) => sp.id === 'nullMark');
  const nullHeld = releases ? Math.max(0, mark.held) * brokenMult : 0;
  // *Du Vide 5* : `max(total, total propre du lancer précédent appliqué)`.
  const voidCarry = hasMaterialBonus(bonuses, 'void', 5)
    ? Math.max(0, (mat?.lastTotal ?? 0) - own - nullHeld)
    : 0;
  // *Silicium 3* (A95-3) : l'excédent du coup fatal précédent s'ajoute aux
  // dégâts **finaux**, après tous les multiplicateurs et *Du Vide 5*. v0.57.0
  // (A122-2) : il reste compris dans `total` (l'interface affiche le total
  // complet), mais `Game.applyThrow` ne l'inflige qu'une fois, sans Mutilant.
  const overkillCarry =
    hasMaterialBonus(bonuses, 'silicon', 3) && (mat?.overkillCarry ?? 0) > 0
      ? (mat?.overkillCarry ?? 0)
      : 0;
  /**
   * Super-Méca Gobelin, Tricorne (v0.49.0) : armure de `armorStacks` crans
   * (un par lancer déjà appliqué, plafonné) — les dégâts du lancer sont
   * divisés par `perHit^crans` avant application.
   */
  const armor = findEffect(enemy, 'armor');
  const armorDivisor = armor
    ? Math.pow(Math.max(1, armor.perHit), Math.min(Math.max(0, enemy?.armorStacks ?? 0), armor.maxStacks))
    : 1;
  const total = (own + nullHeld + voidCarry + overkillCarry) / armorDivisor;

  // Compatibilité `src/game` : le drapeau du lancer est recopié sur chaque dé
  // qui a réellement porté (les dés absorbés restent à `false`).
  if (crit) for (const d of dice) d.crit = !d.absorbed;

  return {
    id,
    dice,
    total,
    spells: outcome.spells,
    spell: outcome.summary,
    spellSeconds: outcome.seconds,
    spellGoldMult: outcome.goldMult,
    spellEvents: [],
    crit,
    critMult: DC,
    spellMult: DS,
    subtotal: DL,
    flatBonus: flat,
    byMinion: opts.byMinion === true,
    critStreak,
    spellStreak,
    hits: opts.hits === 2 ? 2 : 1,
    ...(backhoe > 0 ? { backhoe } : {}),
    ...(releases && mark ? { nullRelease: Math.max(0, mark.held) } : {}),
    breakdown: {
      baseMaster,
      baseBasic,
      flat,
      dieLevelBonus,
      corruptedBonus,
      DL,
      DC,
      DS,
      MP,
      MT,
      affixMult,
      minionMult,
      infernoMult,
      primesMult,
      total,
      critAdditive,
      critMultipliers,
      spellEffectMultipliers,
      critDivisor,
      basicDivisor,
      armorDivisor,
      spellsHalved,
      prepMult: outcome.prepMult,
      blockedSlots: Math.max(0, opts.blockedSlots ?? 0),
      diceMasterMult,
      critStreakMult,
      spellStreakMult,
      backhoeBasic,
      grimoireNumbersMult,
      grimoireFacesMult,
      brokenMult,
      ironBasicMult,
      woodBasicCarry,
      voidCarry,
      overkillCarry,
      electrifyMult,
      electrifyStacks,
      archidicerMult,
      croupierMult,
      affixFactor,
      basicDoubled,
      nullHeld,
      spells: outcome.spells,
    },
  };
}

/**
 * Somme **propre** des faces Basiques d'un lancer (Tractopelle compris, après
 * la Tortue, avant le report de *Boisé 5* et *Ferreux*) — mémoire de *Boisé 5*.
 */
export function ownBasicSum(b: ThrowResult['breakdown']): number {
  const iron = b.ironBasicMult > 0 ? b.ironBasicMult : 1;
  return Math.max(0, b.baseBasic / iron - (b.woodBasicCarry ?? 0));
}

/**
 * Total **propre** d'un lancer, sans les reports de *Du Vide 5* et de
 * *Silicium 3* — mémoire de *Du Vide 5* (pas d'effet cliquet d'un lancer à
 * l'autre). v0.49.0 : ni l'armure, ni les dégâts libérés par *Null*.
 */
export function ownTotal(r: ThrowResult): number {
  const b = r.breakdown;
  // L'armure divise le total après les reports : on la retire d'abord.
  const armor = b.armorDivisor > 0 ? b.armorDivisor : 1;
  const released = r.nullRelease !== undefined ? (b.nullHeld ?? 0) : 0;
  return Math.max(0, r.total * armor - (b.voidCarry ?? 0) - (b.overkillCarry ?? 0) - released);
}

/**
 * Part du **report** de *Silicium 3* dans `r.total` (A122-2) : le report reçu
 * (`breakdown.overkillCarry`) divisé par l'armure, comme le reste du total.
 */
export function carryOf(r: ThrowResult): number {
  const b = r.breakdown;
  const armor = b.armorDivisor > 0 ? b.armorDivisor : 1;
  return Math.max(0, (b.overkillCarry ?? 0) / armor);
}

/**
 * Dégâts **propres** d'un lancer tels qu'appliqués (A122-2) : `r.total` sans
 * le report de *Silicium 3* reçu (armure comprise). Base du poison, de la
 * marque de *Null* et du prochain report.
 */
export function ownDamage(r: ThrowResult): number {
  return Math.max(0, r.total - carryOf(r));
}

/**
 * Dégâts de poison par seconde posés par un lancer — COMMENTS v0.2.2 §4.
 * Somme des valeurs *Empoisonné* des dés Maîtres du lancer (dés absorbés exclus),
 * plafonnée à `poisonCap` (30 %), appliquée aux **dégâts finaux du lancer**.
 * v0.57.0 (A122-2) : hors report de *Silicium 3* (`ownDamage`).
 * v0.58.0 (A123-4) : le facteur d'affixes du lancer (*Rat-Ailé*, ×1,2,
 * `breakdown.affixFactor`) relève les valeurs **et** le plafond (30 % → 36 %).
 */
export function poisonOf(
  result: ThrowResult,
  inventory: readonly MasterDie[],
  poisonCap: number = BALANCE.poisonCap,
): number {
  const factor = affixFactorOf(result);
  let pct = 0;
  for (const d of result.dice) {
    // Un rebond (*Caoutchouc*) ou une copie (*42*, *Réplicant*) ne double pas l'affixe de son dé.
    if (d.absorbed || !d.isMaster || !d.dieId || isExtraRoll(d)) continue;
    const die = inventory.find((x) => x.id === d.dieId);
    if (die) pct += affixValue(die, 'poisoned', factor);
  }
  if (pct <= 0) return 0;
  return Math.min(pct, poisonCap * factor) * ownDamage(result);
}

/**
 * Bonus apportés par les dés Maîtres présents dans le lancer qui tue :
 * Doré (or) et Temporel (secondes) — DECISIONS G14 / D.8.
 *
 * *Temporel* est devenu une **chance** : chaque instance d'affixe est tirée
 * séparément et accorde `BALANCE.temporalSeconds` (1 s) si elle sort.
 */
/**
 * *Mutilant* (COMMENTS v0.6.0 §1.3) : chaque instance de l'affixe portée par un
 * dé Maître **qui a touché** (dé absorbé exclu) est tirée séparément ; la
 * première qui sort mutile l'ennemi. Renvoie `true` si le lancer mutile.
 */
export function mutilationRoll(
  result: ThrowResult,
  inventory: readonly MasterDie[],
  rng: Rng,
): boolean {
  let mutilates = false;
  // A123-4 : facteur d'affixes du lancer (Rat-Ailé).
  const factor = affixFactorOf(result);
  for (const d of result.dice) {
    // Un rebond (*Caoutchouc*) ou une copie (*42*, *Réplicant*) ne double pas l'affixe de son dé.
    if (d.absorbed || !d.isMaster || !d.dieId || isExtraRoll(d)) continue;
    const die = inventory.find((x) => x.id === d.dieId);
    if (!die) continue;
    for (const affix of die.affixes) {
      if (affix.id !== 'mutilating') continue;
      // Dé Enrichi (A94) : chance × 1,5.
      if (rng.chance(affixValueOf(die, affix, factor))) mutilates = true;
    }
  }
  return mutilates;
}

export function killBonuses(
  result: ThrowResult,
  inventory: readonly MasterDie[],
  rng: Rng,
): { goldBonus: number; seconds: number } {
  const masters = mastersOf(result, inventory);
  // A123-4 : facteur d'affixes du lancer (Rat-Ailé) sur Temporel et Doré.
  const factor = affixFactorOf(result);
  let seconds = 0;
  for (const die of masters) {
    for (const affix of die.affixes) {
      if (affix.id !== 'temporal') continue;
      // Dé Enrichi (A94) : chance × 1,5.
      if (rng.chance(affixValueOf(die, affix, factor))) seconds += BALANCE.temporalSeconds;
    }
  }
  return { goldBonus: sumAffix(masters, 'golden', factor), seconds };
}
